/**
 * Bot 详情子路由：路径 <-> 旧 tab key 映射。
 * 旧链 `?tab=analytics-credentials` 启动时会 redirect 到 `/bots/:id/credentials`。
 */

export type AnalyticsNavSection = "setup" | "data" | "diagnose";

/** 分析 bot 侧栏：域 -> 子路径（URL 段） */
export const ANALYTICS_NAV: Record<
  AnalyticsNavSection,
  ReadonlyArray<{ readonly path: string; readonly label: string }>
> = {
  setup: [
    { path: "basic", label: "基础设置" },
    { path: "chats", label: "频道/群聊" },
    { path: "credentials", label: "平台凭据" },
    { path: "ads", label: "广告列表" },
    { path: "pool", label: "链接池" },
  ],
  data: [
    { path: "overview", label: "概览" },
    { path: "chat-funnel", label: "群组漏斗" },
    { path: "member-reconcile", label: "人数对账" },
    { path: "ads-geo", label: "广告与地域" },
    { path: "conversions", label: "转化记录" },
    { path: "clicks", label: "访问记录" },
  ],
  diagnose: [
    { path: "diagnosis", label: "链接诊断" },
    { path: "untracked-joins", label: "未归因入群" },
  ],
};

/** 旧 Tabs value -> 子路径 */
export const TAB_TO_PATH: Record<string, string> = {
  basic: "basic",
  wheel: "wheel",
  languages: "languages",
  prizes: "prizes",
  commands: "commands",
  push: "push",
  "invite-claims": "invite-claims",
  fulfillment: "fulfillment",
  payment: "payment",
  channels: "channels",
  texts: "texts",
  users: "users",
  stats: "stats",
  "analytics-chats": "chats",
  "analytics-credentials": "credentials",
  "analytics-ads": "ads",
  "analytics-pool": "pool",
  "analytics-conversions": "conversions",
  "analytics-clicks": "clicks",
  /** 旧「报表」整页 -> 概览 */
  "analytics-stats": "overview",
  stats_analytics: "overview",
  overview: "overview",
  "chat-funnel": "chat-funnel",
  "member-reconcile": "member-reconcile",
  "ads-geo": "ads-geo",
  "analytics-link-diagnosis": "diagnosis",
  "analytics-untracked-joins": "untracked-joins",
};

/** 子路径 -> 旧 Tabs value（兼容内部 setActiveTab 调用） */
export const PATH_TO_TAB: Record<string, string> = Object.fromEntries(
  Object.entries(TAB_TO_PATH).map(([tab, path]) => [path, tab]),
);

export const ANALYTICS_PATHS = new Set(
  Object.values(ANALYTICS_NAV).flatMap((items) => items.map((x) => x.path)),
);

export const LOTTERY_PATHS = new Set([
  "basic",
  "wheel",
  "languages",
  "prizes",
  "commands",
  "push",
  "invite-claims",
  "fulfillment",
  "payment",
  "channels",
  "texts",
  "users",
  "stats",
]);

export const analyticsSectionOfPath = (path: string): AnalyticsNavSection => {
  // 旧 /stats 在分析 bot 下视为数据区
  if (path === "stats") return "data";
  if (ANALYTICS_NAV.data.some((x) => x.path === path)) return "data";
  if (ANALYTICS_NAV.diagnose.some((x) => x.path === path)) return "diagnose";
  return "setup";
};

export const botSectionHref = (botId: string, path: string, search = ""): string => {
  const q = search.startsWith("?") || search === "" ? search : `?${search}`;
  return `/bots/${botId}/${path}${q}`;
};

export const tabToPath = (tab: string): string => TAB_TO_PATH[tab] ?? tab;

/** 面包屑中文 */
export const BOT_SECTION_LABELS: Record<string, string> = {
  basic: "基础设置",
  wheel: "转盘",
  languages: "语言",
  prizes: "奖品池",
  commands: "命令",
  push: "推送",
  "invite-claims": "邀请申报",
  fulfillment: "履约",
  payment: "支付",
  channels: "频道",
  texts: "文案",
  users: "用户",
  stats: "报表",
  overview: "概览",
  "chat-funnel": "群组漏斗",
  "member-reconcile": "人数对账",
  "ads-geo": "广告与地域",
  chats: "频道/群聊",
  credentials: "平台凭据",
  ads: "广告列表",
  pool: "链接池",
  conversions: "转化记录",
  clicks: "访问记录",
  diagnosis: "链接诊断",
  "untracked-joins": "未归因入群",
  facebook: "Facebook",
  tiktok: "TikTok",
  google: "Google",
  sharechat: "ShareChat",
};
